import { useEffect, useState, useRef } from 'react';
import { IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckmarkCircle2Icon,
  CloseIcon,
  InfoCircleIcon,
} from '#root/icons';

import { Toast as ToastType } from '#root/hooks/useToastQueue';

const variantIcon = {
  success: CheckmarkCircle2Icon,
  warning: AlertTriangleIcon,
  error: AlertCircleIcon,
  info: InfoCircleIcon,
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    backdropFilter: 'blur(20px)',
    border: '1px solid',
    minWidth: 320,
    maxWidth: 420,
    fontSize: 14,
    fontFamily: 'var(--font-family)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'default',
    position: 'relative',
    overflow: 'hidden',

    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
    },

    // Mobile: Full width dentro del contenedor 90vw
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
      maxWidth: '100%',
      margin: 0, // Sin margin porque el contenedor ya maneja el ancho
    },
  },

  // Variantes sutiles con transparencia
  success: {
    // backgroundColor: 'rgba(76, 175, 80, 0.12)',
    backgroundColor: 'oklch(97.9% 0.021 166.113 / 0.8)', // tw: emerald-50/80
    borderColor: 'oklch(43.2% 0.095 166.913 / 0.8)', // tw: emerald-800/80
    color: 'oklch(43.2% 0.095 166.913)', // tw: emerald-800
  },
  error: {
    backgroundColor: 'oklch(97.1% 0.013 17.38 / 0.8)', // tw: red-50/80
    borderColor: 'oklch(44.4% 0.177 26.899 / 0.8)', // tw: red-800/80
    color: 'oklch(44.4% 0.177 26.899)', // tw: red-800
  },
  info: {
    backgroundColor: 'oklch(98.4% 0.019 200.873 / 0.8)', // tw: cyan-50/80
    borderColor: 'oklch(45% 0.085 224.283 / 0.8)', // tw: cyan-800/80
    color: 'oklch(45% 0.085 224.283)', // tw: cyan-800
  },
  warning: {
    backgroundColor: 'oklch(98.7% 0.026 102.212 / 0.8)', // tw: yellow-50/80
    borderColor: 'oklch(47.6% 0.114 61.907 / 0.8)', // tw: yellow-800/80
    color: 'oklch(47.6% 0.114 61.907)', // tw: yellow-800
  },

  icon: {
    fontSize: 18,
    flexShrink: 0,
    opacity: 0.9,
  },

  message: {
    flex: 1,
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 600,
  },

  closeButton: {
    padding: 4,
    marginRight: -8,
    '& svg': {
      fontSize: 18,
    },
  },

  // Progress bar (opcional)
  progressBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: 2,
    backgroundColor: 'currentColor',
    opacity: 0.3,
    transition: 'width linear',
  },

  // Animaciones de entrada/salida
  entering: {
    animation: '$slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  exiting: {
    animation: '$fadeOutScale 0.3s cubic-bezier(0.4, 0, 1, 1)',
  },

  '@keyframes slideInRight': {
    '0%': {
      transform: 'translateX(100%) scale(0.8)',
      opacity: 0,
    },
    '100%': {
      transform: 'translateX(0) scale(1)',
      opacity: 1,
    },
  },

  '@keyframes fadeOutScale': {
    '0%': {
      transform: 'scale(1)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(0.8)',
      opacity: 0,
    },
  },
}));

interface ToastProps {
  toast: ToastType;
  onClose: (id: string) => void;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  showProgress?: boolean;
}

const Toast = (props: ToastProps) => {
  const { toast, onClose, onPause, onResume, showProgress = false } = props;
  const classes = useStyles();
  const Icon = variantIcon[toast.variant];

  const [isExiting, setIsExiting] = useState(false);
  const [progressWidth, setProgressWidth] = useState(100);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(toast.id);
    }, 200); // Duración de animación de salida
  };

  // Progress bar animation
  useEffect(() => {
    if (!showProgress || !toast.duration || toast.pausedAt) return;

    const startTime = Date.now();
    const duration = toast.duration;

    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, duration - elapsed);
      const percentage = (remaining / duration) * 100;

      setProgressWidth(percentage);

      if (percentage <= 0 && progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    }, 50);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [showProgress, toast.duration, toast.pausedAt]);

  return (
    <div
      className={clsx(
        classes.root,
        classes[toast.variant],
        !isExiting && classes.entering,
        isExiting && classes.exiting
      )}
      onMouseEnter={() => onPause(toast.id)}
      onMouseLeave={() => onResume(toast.id)}
      role="alert"
      aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}>
      <Icon className={classes.icon} />
      <div className={classes.message}>{toast.message}</div>
      <IconButton
        aria-label="close"
        size="small"
        className={classes.closeButton}
        onClick={handleClose}>
        <CloseIcon />
      </IconButton>

      {/* Progress bar opcional */}
      {showProgress && toast.duration && (
        <div
          className={classes.progressBar}
          style={{
            width: `${progressWidth}%`,
            transitionDuration: toast.pausedAt ? '0s' : '50ms',
          }}
        />
      )}
    </div>
  );
};

export default Toast;
